import { useState } from 'react'
import toast from 'react-hot-toast'
import Modal from 'components/modal'
import { Button } from 'components/common'

export type ImportColumn = {
  name: string
  required: boolean
  /** Static configs: a short example value shown in the table */
  example?: string
  /** API-sourced: human-readable format/purpose description */
  description?: string
  /** API-sourced: data type of the column */
  type?: 'string' | 'boolean' | 'integer' | 'number' | 'date' | 'multi-value'
  /** API-sourced: whether the keyword EMPTY is accepted to clear the field */
  supportsEmpty?: boolean
  /** API-sourced: fixed set of accepted values, if applicable */
  allowedValues?: string[]
  /** API-sourced: delimiter for multi-value columns */
  delimiter?: string
}

type Props = {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  moduleName: string
  columns: ImportColumn[]
  /** Static file URL — used when the sample CSV is a hosted static asset */
  sampleFileUrl?: string
  /** API-based download — called when the sample CSV must be fetched from the server */
  onDownloadSample?: () => Promise<void>
}

const TYPE_LABEL: Record<string, string> = {
  string: 'text',
  boolean: '0 / 1',
  integer: 'integer',
  number: 'number',
  date: 'DD/MM/YYYY',
  'multi-value': 'multi',
}

const ImportModal = ({
  isOpen,
  onClose,
  onConfirm,
  moduleName,
  columns,
  sampleFileUrl,
  onDownloadSample,
}: Props) => {
  const [downloading, setDownloading] = useState(false)

  const hasDownloadHandler = !!(sampleFileUrl || onDownloadSample)

  // Use "Description" header when columns come from the API (have description field)
  const hasDescriptions = columns.some((c) => c.description)

  const handleDownload = async () => {
    if (onDownloadSample) {
      setDownloading(true)
      const toastId = toast.loading('Downloading sample file...', {
        duration: Infinity,
      })
      try {
        await onDownloadSample()
        toast.success('Sample file downloaded', { id: toastId, duration: 3000 })
      } catch {
        toast.error('Failed to download sample file', {
          id: toastId,
          duration: 3000,
        })
      } finally {
        setDownloading(false)
      }
    } else if (sampleFileUrl) {
      const link = document.createElement('a')
      link.href = sampleFileUrl
      link.download = `${moduleName.replace(/\s+/g, '_')}_sample.csv`
      link.click()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      title={`Import ${moduleName}`}
      width={700}
    >
      <>
        <p className="mb-4 text-sm text-gray-500">
          Your CSV file must include the following columns:
        </p>

        <div className="max-h-[320px] overflow-y-auto rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-gray-200 bg-gray-50 text-left">
                <th className="px-4 py-2.5 font-semibold text-gray-600">
                  Column Name
                </th>
                <th className="px-4 py-2.5 font-semibold text-gray-600">
                  {hasDescriptions ? 'Description' : 'Example Value'}
                </th>
                <th className="px-4 py-2.5 font-semibold text-gray-600">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {columns.map((col) => {
                const info = col.description ?? col.example ?? ''
                const typeLabel = col.type ? TYPE_LABEL[col.type] : null

                return (
                  <tr key={col.name} className="hover:bg-gray-50">
                    {/* Column name + optional type badge */}
                    <td className="px-4 py-3 align-top">
                      <span className="font-mono text-xs font-medium text-gray-900">
                        {col.name}
                      </span>
                      {typeLabel && (
                        <span className="ml-2 inline-flex rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                          {typeLabel}
                        </span>
                      )}
                    </td>

                    {/* Description / example + allowed values */}
                    <td className="px-4 py-3 align-top">
                      <p className="text-sm text-gray-500">{info}</p>
                      {col.allowedValues && col.allowedValues.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {col.allowedValues.slice(0, 5).map((v) => (
                            <span
                              key={v}
                              className="inline-flex rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600"
                            >
                              {v}
                            </span>
                          ))}
                          {col.allowedValues.length > 5 && (
                            <span className="inline-flex rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                              +{col.allowedValues.length - 5} more
                            </span>
                          )}
                        </div>
                      )}
                    </td>

                    {/* Required / Optional + EMPTY badge */}
                    <td className="px-4 py-3 align-top">
                      <div className="flex flex-col gap-1">
                        {col.required ? (
                          <span className="inline-flex w-fit rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                            Required
                          </span>
                        ) : (
                          <span className="inline-flex w-fit rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">
                            Optional
                          </span>
                        )}
                        {col.supportsEmpty && (
                          <span className="inline-flex w-fit rounded-full bg-yellow-50 px-2.5 py-0.5 text-xs font-medium text-yellow-600">
                            EMPTY clears
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <Button
            buttonText="Download sample .csv"
            variant="outline"
            type="button"
            loading={downloading}
            disabled={!hasDownloadHandler}
            onClick={handleDownload}
          />

          <div className="flex items-center gap-3">
            <Button
              buttonText="Cancel"
              variant="outline"
              type="button"
              onClick={onClose}
            />
            <Button
              buttonText="Choose file →"
              type="button"
              onClick={onConfirm}
            />
          </div>
        </div>
      </>
    </Modal>
  )
}

export default ImportModal
